import { TimeUnit } from './apollo/graphqlSchema';
import { capitalize, isNil, lowerCase } from 'lodash';
import { assertNotNil, hasProperty } from './types';
import { MaybeNil } from './interfaces';

interface TimeUnitText {
  singular: string;
  plural: string;
  adverb: string;
}

const timeUnitToText: Record<TimeUnit, TimeUnitText> = {
  [TimeUnit.Second]: {
    singular: 'second',
    plural: 'seconds',
    adverb: 'secondly',
  },
  [TimeUnit.Minute]: {
    singular: 'minute',
    plural: 'minutes',
    adverb: 'minutely',
  },
  [TimeUnit.Hour]: {
    singular: 'hour',
    plural: 'hours',
    adverb: 'hourly',
  },
  [TimeUnit.Day]: {
    singular: 'day',
    plural: 'days',
    adverb: 'daily',
  },
  [TimeUnit.Week]: {
    singular: 'week',
    plural: 'weeks',
    adverb: 'weekly',
  },
  [TimeUnit.FourWeek]: {
    singular: '4-week',
    plural: '4 weeks',
    adverb: '4-weekly',
  },
  [TimeUnit.Month]: {
    singular: 'month',
    plural: 'months',
    adverb: 'monthly',
  },
  [TimeUnit.Quarter]: {
    singular: 'quarter',
    plural: 'quarters',
    adverb: 'quarterly',
  },
  [TimeUnit.Year]: {
    singular: 'year',
    plural: 'years',
    adverb: 'yearly',
  },
  [TimeUnit.AllTime]: {
    singular: 'all time',
    plural: 'all time',
    adverb: 'all time',
  },
  [TimeUnit.Na]: {
    singular: 'n/a',
    plural: 'n/a',
    adverb: 'n/a',
  },
};

type IRelaxedTimeUnit = MaybeNil<TimeUnit | string>;

export const getTimeUnitText = (timeUnit: IRelaxedTimeUnit): TimeUnitText => {
  if (isNil(timeUnit)) {
    return timeUnitToText[TimeUnit.Na];
  } else if (hasProperty(timeUnitToText, timeUnit)) {
    return timeUnitToText[timeUnit];
  } else {
    // this should not happen due to exhaustive typescript matching of all
    // time unit values. However, Kobol may introduce a new enum that Caprica
    // is not yet aware, thus instead of crashing, try to generate a value
    const derivedText = lowerCase(timeUnit);
    return {
      singular: derivedText,
      plural: `${derivedText}s`,
      adverb: `${derivedText}ly`,
    };
  }
};

export const getCapitalizedTimeUnitText = (
  timeUnit: IRelaxedTimeUnit,
): TimeUnitText => {
  const timeUnitText = getTimeUnitText(timeUnit);
  return {
    singular: capitalize(timeUnitText.singular),
    adverb: capitalize(timeUnitText.adverb),
    plural: capitalize(timeUnitText.plural),
  };
};

/**
 * Nulls mean "can't convert this time unit as TimeUnit.Second
 */
const secondsUnitMultiplier = {
  [TimeUnit.AllTime]: null,
  [TimeUnit.Day]: 24 * 60 * 60,
  [TimeUnit.FourWeek]: null,
  [TimeUnit.Hour]: 60 * 60,
  [TimeUnit.Minute]: 60,
  [TimeUnit.Month]: null,
  [TimeUnit.Quarter]: null,
  [TimeUnit.Na]: null,
  [TimeUnit.Second]: 1,
  [TimeUnit.Week]: 7 * 24 * 60 * 60,
  [TimeUnit.Year]: null,
} as const;

/**
 * Number of units, returns number in TimeUnit.Second
 */
export const timeUnitValueToSeconds = (val: number, unit: TimeUnit) => {
  const multiplier = secondsUnitMultiplier[unit];
  assertNotNil(multiplier);
  return val * multiplier;
};
